import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import MyLoader from "../Spinner/MyLoader";
import MdlSelectSingleStudent from "./MdlSelectSingleStudent";
import "../css/Admin_css/admin-customize.css";

const MdlEventParticipantRegistration = ({
  show,
  onClose,
  selectedStudents: initialSelectedStudents,
  event_id,
  session_id,
  eventPrograms,
  participantGroups,
  selectedGroup,
  onStudentsSelected,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [participatingClasses, setParticipatingClasses] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [registrationData, setRegistrationData] = useState({});
  const [selectedStudents, setSelectedStudents] = useState(initialSelectedStudents || []);
  const [showStudentSelection, setShowStudentSelection] = useState(false); // Don't auto-open student selection
  const studentJustSelectedRef = useRef(false);

  const hasStudents = selectedStudents && selectedStudents.length > 0;
  const currentStudent = hasStudents ? selectedStudents[currentStudentIndex] : null;
  const studentId = currentStudent ? currentStudent.student_enrollment_no : null;
  const data = studentId ? (registrationData[studentId] || {}) : {};

  const getParticipatingClasses = useCallback(async () => {
    if (!event_id || !session_id) return;
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getEventClasses.php`;
    try {
      const response = await axios.get(URL, {
        params: { event_id, session_id, school_code: SCHOOL_CODE },
      });
      if (response.data.api_status === "OK") {
        setParticipatingClasses(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching participating classes:", error);
    }
  }, [event_id, session_id]);

  const getAllClasses = useCallback(async () => {
    if (!session_id) return;
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getClass.php`;
    try {
      const response = await axios.get(URL, {
        params: { school_code: SCHOOL_CODE, session_id },
      });
      if (response.data.api_status === "OK") {
        setAllClasses(response.data.data?.class || response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  }, [session_id]);

  useEffect(() => {
    if (initialSelectedStudents) {
      setSelectedStudents(initialSelectedStudents);
    }
  }, [initialSelectedStudents]);

  useEffect(() => {
    if (show) {
      if (hasStudents) {
        // Students are selected, ensure registration data is initialized
        if (Object.keys(registrationData).length === 0) {
          const initialData = {};
          selectedStudents.forEach((student) => {
            initialData[student.student_enrollment_no] = {
              program_id: "",
              group_id: selectedGroup?.group_id || participantGroups[0]?.group_id || "",
              participating_class_id: "",
              remark: "",
            };
          });
          setRegistrationData(initialData);
        }
        // Fetch classes if not already loaded
        if (participatingClasses.length === 0) {
          getParticipatingClasses();
        }
        if (allClasses.length === 0) {
          getAllClasses();
        }
      } else {
        // No students selected - fetch classes anyway for when student is selected
        if (participatingClasses.length === 0) {
          getParticipatingClasses();
        }
        if (allClasses.length === 0) {
          getAllClasses();
        }
      }
    } else {
      // Modal closed, reset
      setShowStudentSelection(false);
    }
  }, [show, hasStudents, selectedStudents, registrationData, participatingClasses.length, allClasses.length, getParticipatingClasses, getAllClasses]);

  const handleStudentSelected = useCallback((studentEnrollmentNo, student, sessionId, sessionName) => {
    if (!student) {
      myToast.notify_warning("Please select a student");
      return;
    }
    // Note: studentJustSelectedRef is set in action2 callback before this is called
    // Wrap single student in array for consistency
    const students = [student];
    
    // Initialize registration data
    const initialData = {
      [student.student_enrollment_no]: {
        program_id: "",
        group_id: selectedGroup?.group_id || participantGroups[0]?.group_id || "",
        participating_class_id: "",
        remark: "",
      }
    };
    
    // Update all state at once
    setSelectedStudents(students);
    setCurrentStudentIndex(0);
    setRegistrationData(initialData);
    setShowStudentSelection(false); // Close student selection, show registration form
    
    // Fetch data for registration form
    getParticipatingClasses();
    getAllClasses();
    
    if (onStudentsSelected) {
      onStudentsSelected(students);
    }
  }, [getParticipatingClasses, getAllClasses, onStudentsSelected]);

  const handleInputChange = useCallback((studentId, field, value) => {
    setRegistrationData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
      },
    }));
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const formatTime = useCallback((timeString) => {
    return timeString || "";
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStudentIndex((prev) => 
      prev < selectedStudents.length - 1 ? prev + 1 : prev
    );
  }, [selectedStudents.length]);

  const handlePrevious = useCallback(() => {
    setCurrentStudentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!currentStudent) return;
    const studentId = currentStudent.student_enrollment_no;
    const studentData = registrationData[studentId];

    if (!studentData?.group_id) {
      myToast.notify_warning("Please select a Group");
      return;
    }

    if (!studentData?.participating_class_id) {
      myToast.notify_warning("Please select a Participating Class/Stream");
      return;
    }

    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}AddEventParticipantFromSchool.php`;

    try {
      const params = {
        event_id,
        session_id,
        school_code: SCHOOL_CODE,
        group_id: studentData.group_id || selectedGroup?.group_id || participantGroups[0]?.group_id,
        student_ids: JSON.stringify([studentId]),
        participating_class_id: studentData.participating_class_id,
        ...(studentData.program_id && { program_id: studentData.program_id }),
        ...(studentData.remark && { remark: studentData.remark }),
      };

      console.log("Calling API:", URL);
      console.log("API Params:", params);

      const response = await axios.get(URL, { params });

      console.log("API Response:", response.data);

      if (response.data.api_status === "OK" || response.data.api_status === "PARTIAL") {
        const successCount = response.data.success_count || 0;
        if (successCount > 0) {
          myToast.notify_success("Registration completed successfully");
          if (currentStudentIndex < selectedStudents.length - 1) {
            handleNext();
          } else {
            if (onSuccess) onSuccess();
            onClose();
          }
        } else {
          myToast.notify_warning(response.data.api_remark || "Failed to register participant");
        }
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to register participant");
      }
    } catch (error) {
      console.error("Error registering participant:", error);
      myToast.notify_error("Error registering participant: " + (error.response?.data?.api_remark || error.message));
    } finally {
      setLoading(false);
    }
  }, [currentStudent, registrationData, event_id, session_id, selectedGroup, participantGroups, currentStudentIndex, selectedStudents.length, handleNext, onSuccess, onClose]);

  const availableClasses = participatingClasses.length > 0 ? participatingClasses : allClasses;

  if (!show) {
    return null;
  }

  // Main registration modal - always render when show is true
  return (
    <>
      {/* Student selection modal overlay */}
      {showStudentSelection && (
        <MdlSelectSingleStudent
          action={() => {
            // If student was just selected, don't close main modal - just close student selection
            if (studentJustSelectedRef.current) {
              studentJustSelectedRef.current = false;
              setShowStudentSelection(false);
              return;
            }
            // User closed without selecting - just close student selection, keep main modal open
            setShowStudentSelection(false);
          }}
          action2={(studentEnrollmentNo, student, sessionId, sessionName) => {
            // Mark that student is being selected BEFORE calling handleStudentSelected
            studentJustSelectedRef.current = true;
            // Handle student selection
            handleStudentSelected(studentEnrollmentNo, student, sessionId, sessionName);
          }}
        />
      )}
      <style>{`
        .registration-modal .modal-dialog {
          overflow: visible;
        }
        .registration-modal .modal-content {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 18px 40px rgba(15,23,42,0.28);
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .registration-modal .modal-header {
          border-radius: 12px 12px 0 0;
          overflow: hidden;
          flex-shrink: 0;
        }
        .registration-modal .modal-body {
          background: #fff;
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          flex: 1 1 auto;
          min-height: 0;
        }
        .registration-modal .modal-footer {
          flex-shrink: 0;
          border-top: 1px solid ${COLORS.grey3 || "#e2e8f0"};
        }
        .registration-modal .form-group {
          position: relative;
          margin-bottom: 24px;
        }
        .registration-modal select.form-control {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 12px;
          padding-right: 40px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 14px;
          line-height: 1.5;
          min-height: 44px;
          z-index: 1;
        }
        .registration-modal select.form-control:focus {
          outline: none;
          border-color: ${COLORS.primary_color_shade6 || "#3b82f6"};
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .registration-modal select.form-control:hover {
          border-color: ${COLORS.primary_color_shade5 || "#60a5fa"};
        }
        .registration-modal select.form-control option {
          padding: 12px 16px;
          background: #fff;
          color: #334155;
          font-size: 14px;
        }
        .registration-modal select.form-control option:hover {
          background: ${COLORS.primary_color_shade1 || "#E3F2FD"};
        }
        .registration-modal select.form-control option:checked {
          background: ${COLORS.primary_color_shade6 || "#3b82f6"};
          color: #fff;
        }
        /* Ensure dropdowns can overflow modal body */
        .registration-modal .modal-body select.form-control {
          position: relative;
        }
        /* Custom scrollbar for modal body */
        .registration-modal .modal-body::-webkit-scrollbar {
          width: 8px;
        }
        .registration-modal .modal-body::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .registration-modal .modal-body::-webkit-scrollbar-thumb {
          background: ${COLORS.grey4 || "#cbd5e1"};
          border-radius: 4px;
        }
        .registration-modal .modal-body::-webkit-scrollbar-thumb:hover {
          background: ${COLORS.grey5 || "#94a3b8"};
        }
        /* Modern dropdown styling */
        .registration-modal .form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
          font-size: 14px;
          font-weight: 600;
          color: ${COLORS.black8 || "#1e293b"};
        }
      `}</style>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        centered
        backdrop={true}
        keyboard={true}
        dialogClassName="registration-modal"
        animation={false}
        enforceFocus={false}
      >
        <Modal.Header
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
            color: "#fff",
            borderBottom: "none",
          }}
        >
          <Modal.Title style={{ color: "#fff", fontWeight: 700 }}>
            <i className="fa fa-user-plus" style={{ marginRight: "8px" }} />
            Participant Registration
            {selectedStudents.length > 1 && (
              <span style={{ fontSize: "14px", fontWeight: 500, marginLeft: "12px" }}>
                ({currentStudentIndex + 1} of {selectedStudents.length})
              </span>
            )}
          </Modal.Title>
          <button
            type="button"
            className="close"
            onClick={onClose}
            style={{ color: "#fff", opacity: 1, fontSize: "28px" }}
          >
            <span>&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px", background: "#fff" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <MyLoader />
            </div>
          ) : !hasStudents ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade1 || "#E3F2FD"} 0%, ${COLORS.white} 100%)`,
                padding: "40px",
                borderRadius: "16px",
                border: `2px solid ${COLORS.primary_color_shade3 || "#90CAF9"}`,
                marginBottom: "20px",
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                  boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
                }}>
                  <i className="fa fa-users" style={{ fontSize: "36px", color: "#fff" }} />
                </div>
                <h5 style={{ 
                  color: COLORS.primary_color_shade8, 
                  fontWeight: 700, 
                  marginBottom: "12px" 
                }}>
                  Select Students
                </h5>
                <p style={{ 
                  color: "#64748b", 
                  marginBottom: "32px",
                  fontSize: "14px"
                }}>
                  Click the button below to select students for event registration
                </p>
                <button
                  type="button"
                  onClick={() => setShowStudentSelection(true)}
                  style={{
                    padding: "14px 32px",
                    borderRadius: "12px",
                    border: "none",
                    background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "16px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                  }}
                >
                  <i className="fa fa-user-plus" style={{ marginRight: "8px" }} />
                  Select Students
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade1 || "#E3F2FD"} 0%, ${COLORS.white} 100%)`,
                padding: "20px",
                borderRadius: "12px",
                marginBottom: "24px",
                border: `2px solid ${COLORS.primary_color_shade3 || "#90CAF9"}`,
              }}>
                <h6 style={{ marginBottom: "16px", color: COLORS.primary_color_shade8, fontWeight: 700 }}>
                  <i className="fa fa-user" style={{ marginRight: "8px" }} />
                  Student Information
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Name:</strong> {currentStudent.stu_name}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Father Name:</strong> {currentStudent.father_name || "N/A"}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Mother Name:</strong> {currentStudent.mother_name || "N/A"}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Birthday:</strong> {currentStudent.dob || currentStudent.student_dob ? formatDate(currentStudent.dob || currentStudent.student_dob) : "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Gender:</strong> {currentStudent.gender || "N/A"}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Category:</strong> {currentStudent.community || "N/A"}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Religion:</strong> {currentStudent.religion || "N/A"}
                    </p>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Phone:</strong> {currentStudent.contact_number || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: "24px", position: "relative" }}>
                <label style={{ fontWeight: 600, marginBottom: "10px", color: COLORS.black8, display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa fa-calendar-alt" style={{ fontSize: "14px", color: COLORS.primary_color_shade6 }} />
                  Select Program Schedule
                </label>
                <select
                  className="form-control"
                  value={data.program_id || ""}
                  onChange={(e) => handleInputChange(studentId, "program_id", e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: `2px solid ${data.program_id ? COLORS.primary_color_shade6 : COLORS.grey3}`,
                    padding: "12px 40px 12px 16px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    color: "#334155",
                    fontWeight: data.program_id ? 500 : 400,
                    width: "100%",
                    boxShadow: data.program_id ? "0 2px 8px rgba(59, 130, 246, 0.1)" : "none",
                  }}
                >
                  <option value="" style={{ padding: "12px" }}>Select Program Schedule (Optional)</option>
                  {eventPrograms.map((program) => (
                    <option key={program.program_id} value={program.program_id} style={{ padding: "12px" }}>
                      {program.program_name} - {formatDate(program.program_date)} {formatTime(program.program_time)} 
                      {program.program_shift ? ` SHIFT:${program.program_shift}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "24px", position: "relative" }}>
                <label style={{ fontWeight: 600, marginBottom: "10px", color: COLORS.black8, display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa fa-users" style={{ fontSize: "14px", color: COLORS.primary_color_shade6 }} />
                  Select Group <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={data.group_id || selectedGroup?.group_id || participantGroups[0]?.group_id || ""}
                  onChange={(e) => handleInputChange(studentId, "group_id", e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: `2px solid ${data.group_id ? COLORS.primary_color_shade6 : COLORS.grey3}`,
                    padding: "12px 40px 12px 16px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    color: "#334155",
                    fontWeight: data.group_id ? 500 : 400,
                    width: "100%",
                    boxShadow: data.group_id ? "0 2px 8px rgba(59, 130, 246, 0.1)" : "none",
                  }}
                >
                  <option value="" style={{ padding: "12px" }}>Select Group</option>
                  {participantGroups && participantGroups.map((group) => (
                    <option key={group.group_id} value={group.group_id} style={{ padding: "12px" }}>
                      {group.group_name || group.group_id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "24px", position: "relative" }}>
                <label style={{ fontWeight: 600, marginBottom: "10px", color: COLORS.black8, display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa fa-graduation-cap" style={{ fontSize: "14px", color: COLORS.primary_color_shade6 }} />
                  Select Participating Class/Stream <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={data.participating_class_id || ""}
                  onChange={(e) => handleInputChange(studentId, "participating_class_id", e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: `2px solid ${data.participating_class_id ? COLORS.primary_color_shade6 : COLORS.grey3}`,
                    padding: "12px 40px 12px 16px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    color: "#334155",
                    fontWeight: data.participating_class_id ? 500 : 400,
                    width: "100%",
                    boxShadow: data.participating_class_id ? "0 2px 8px rgba(59, 130, 246, 0.1)" : "none",
                  }}
                >
                  <option value="" style={{ padding: "12px" }}>Select Participating Class/Stream</option>
                  {availableClasses.map((classItem) => (
                    <option key={classItem.class_id} value={classItem.class_id} style={{ padding: "12px" }}>
                      {classItem.class_name}
                    </option>
                  ))}
                </select>
              </div>


              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: 600, marginBottom: "8px", color: COLORS.black8 }}>
                  Remarks (Optional)
                </label>
                <textarea
                  className="form-control"
                  value={data.remark || ""}
                  onChange={(e) => handleInputChange(studentId, "remark", e.target.value)}
                  placeholder="Enter any remarks"
                  rows="3"
                  style={{
                    borderRadius: "8px",
                    border: `2px solid ${COLORS.grey3}`,
                    padding: "10px",
                  }}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: `1px solid ${COLORS.grey3}`,
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            background: "#fff",
          }}
        >
          <div>
            {selectedStudents.length > 1 && currentStudentIndex > 0 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="btn btn-secondary"
                style={{
                  marginRight: "10px",
                  padding: "10px 20px",
                  borderRadius: "8px",
                }}
              >
                <i className="fa fa-arrow-left" style={{ marginRight: "6px" }} />
                Previous
              </button>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{
                marginRight: "10px",
                padding: "10px 20px",
                borderRadius: "8px",
              }}
            >
              Cancel
            </button>
            {hasStudents && (
              <>
                {selectedStudents.length > 1 && currentStudentIndex < selectedStudents.length - 1 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="btn btn-primary"
                    style={{
                      padding: "10px 20px",
                      borderRadius: "8px",
                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                      border: "none",
                    }}
                  >
                    Next
                    <i className="fa fa-arrow-right" style={{ marginLeft: "6px" }} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-success"
                    disabled={loading}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "8px",
                    }}
                  >
                    {loading ? (
                      <>
                        <i className="fa fa-spinner fa-spin" style={{ marginRight: "6px" }} />
                        Registering...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-check" style={{ marginRight: "6px" }} />
                        Register
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdlEventParticipantRegistration;
